import {
  ErrorResponse,
  EventsApi,
  FindProgramEventTagsRequest,
  TagList,
} from '@citruscamps/citrus-client'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import { DefaultMainPagination } from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  IInfiniteProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'

const DefaultFetchProps: FetchProps = {
  sort: 'created',
  order: 'DESC',
  query: {},
}

type TagFilterProps = Omit<FindProgramEventTagsRequest, NonQueryAttributes>

interface FetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: TagFilterProps
}

type IProps = Pick<
  IFetchManyProps<string, TagFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'setFetchProps'
>

interface FetchTags {
  data: string[]
  error?: ErrorResponse
  fetchProps: FetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setFetchProps: (value?: FetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchTags = (props: IProps): FetchTags => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<string, TagFilterProps>({
    type: 'tag',
    programId: props.programId,
    fetchProps: props.fetchProps,
    defaultFetchProps: DefaultFetchProps,
    defaultPagination: props.pagination || DefaultMainPagination,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<TagList> => {
      const { programId, query, limit, skip, sort, order, search, signal } = queryParams
      const client = new EventsApi(generateApiConfig())
      return await client.findProgramEventTags(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          value: Array.isArray(query.value) && query.value.length > 0 ? query.value : undefined,
          exclude_value:
            Array.isArray(query.exclude_value) && query.exclude_value.length > 0
              ? query.exclude_value
              : undefined,
        },
        { signal },
      )
    },
  })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setFetchProps,
    setPagination,
    setSearch,
  }
}

type ITagInfiniteProps = Omit<IProps, 'isFilterPersisted'> &
  Pick<IInfiniteProps<string, TagFilterProps>, 'fetchAll'>

interface FetchInfiniteTags extends Omit<FetchTags, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<TagList, ErrorResponse>>
}

export const useFetchInfiniteTags = (props: ITagInfiniteProps): FetchInfiniteTags => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<string, TagFilterProps>({
    type: 'tag',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultFetchProps,
    pagination: props.pagination || DefaultMainPagination,
    fetchAll: props.fetchAll,
    queryFn: async (queryParams): Promise<TagList> => {
      const { programId, query, limit, skip, sort, order, search, signal } = queryParams
      const client = new EventsApi(generateApiConfig())
      return await client.findProgramEventTags(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          value: Array.isArray(query.value) && query.value.length > 0 ? query.value : undefined,
          exclude_value:
            Array.isArray(query.exclude_value) && query.exclude_value.length > 0
              ? query.exclude_value
              : undefined,
        },
        { signal },
      )
    },
  })
  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    fetchNextPage,
    setSearch,
  }
}
